import { Alert, Card, CardBody } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './dashboard.module.css'
import { AppLayout } from '@/components/AppLayout'
import { Skeleton } from '@/components/Skeleton'
import { api, type DashboardStats } from '@/lib/api'
import { getAuthState, isAuthenticated, type User } from '@/lib/auth-store'
import { getRelativeTime, getDomainFromUrl, formatChangeType } from '@/lib/format'

interface RecentChange {
  id: string
  trackedUrlId: string
  urlDisplayName: string | null
  url: string
  changeType: string
  summary: string
  detectedAt: string
}

export const Route = createFileRoute('/dashboard')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: DashboardPage,
})

function getChangeBadgeClass(changeType: string): string {
  if (changeType === 'price_increase') return styles.changeBadgeIncrease ?? ''
  if (changeType === 'price_decrease') return styles.changeBadgeDecrease ?? ''
  if (changeType === 'new_plan' || changeType === 'feature_added' || changeType === 'plan_renamed')
    return styles.changeBadgeNew ?? ''
  if (changeType === 'removed_plan' || changeType === 'feature_removed')
    return styles.changeBadgeRemoved ?? ''
  return styles.changeBadgeDefault ?? ''
}

function DashboardPage() {
  const auth = getAuthState()
  const [user] = useState<User | null>(auth.user)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentChanges, setRecentChanges] = useState<RecentChange[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [statsData, changesData] = await Promise.all([
          api.getDashboardStats(),
          api.getRecentChanges(),
        ])
        setStats(statsData)
        setRecentChanges(changesData.data as RecentChange[])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [])

  if (isLoading) {
    return (
      <AppLayout title="Dashboard">
        {/* Welcome Section */}
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
            <p className={styles.welcomeSubtitle}>
              Here&apos;s an overview of your pricing monitors.
            </p>
          </CardBody>
        </Card>

        {/* Stats Grid Loading */}
        <div className={styles.statsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className={styles.statCard}>
              <CardBody>
                <Skeleton width="3rem" height="2rem" />
                <Skeleton width="80%" height="1rem" />
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Recent Changes Loading */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Recent Changes</h3>
          </div>
          <div className={styles.changesList}>
            {[1, 2, 3].map((i) => (
              <Card key={i} className={styles.changeCard}>
                <CardBody>
                  <div className={styles.changeHeader}>
                    <div className={styles.changeInfo}>
                      <Skeleton width="60%" height="1.25rem" />
                      <Skeleton width="30%" height="1rem" />
                    </div>
                    <Skeleton width="80px" height="24px" />
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      </AppLayout>
    )
  }

  if (error) {
    return (
      <AppLayout title="Dashboard">
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
          </CardBody>
        </Card>
        <Alert variant="danger">{error}</Alert>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Dashboard">
      {/* Welcome Section */}
      <Card className={styles.welcomeCard}>
        <CardBody>
          <h2 className={styles.welcomeTitle}>
            Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
          </h2>
          <p className={styles.welcomeSubtitle}>
            Here&apos;s an overview of your pricing monitors.
          </p>
        </CardBody>
      </Card>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{stats?.totalUrls ?? 0}</div>
            <div className={styles.statLabel}>Tracked URLs</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.statActive}`}>
          <CardBody>
            <div className={styles.statValue}>{stats?.activeUrls ?? 0}</div>
            <div className={styles.statLabel}>Active URLs</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.statChanges}`}>
          <CardBody>
            <div className={styles.statValue}>{stats?.changesLast7Days ?? 0}</div>
            <div className={styles.statLabel}>Recent Changes (7d)</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.statAlerts}`}>
          <CardBody>
            <div className={styles.statValue}>{stats?.alertsSentLast7Days ?? 0}</div>
            <div className={styles.statLabel}>Alerts Sent (7d)</div>
          </CardBody>
        </Card>
      </div>

      {/* Recent Changes Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Recent Changes</h3>
          <Link to="/urls" className={styles.viewAll}>
            View all
          </Link>
        </div>

        {recentChanges.length === 0 ? (
          <Card className={styles.emptyState}>
            <CardBody>
              <div className={styles.emptyIcon}>📊</div>
              <h3 className={styles.emptyTitle}>No recent changes</h3>
              <p className={styles.emptyDescription}>
                Pricing changes detected on your tracked URLs will appear here.
              </p>
            </CardBody>
          </Card>
        ) : (
          <div className={styles.changesList}>
            {recentChanges.map((change) => (
              <Link
                key={change.id}
                to="/urls/$id"
                params={{ id: change.trackedUrlId }}
                className={styles.changeLink}
              >
                <Card className={styles.changeCard}>
                  <CardBody>
                    <div className={styles.changeHeader}>
                      <div className={styles.changeInfo}>
                        <p className={styles.changeUrl}>
                          {change.urlDisplayName ?? getDomainFromUrl(change.url)}
                        </p>
                        <span
                          className={`${styles.changeBadge} ${getChangeBadgeClass(change.changeType)}`}
                        >
                          {formatChangeType(change.changeType)}
                        </span>
                      </div>
                      <p className={styles.changeDate}>{getRelativeTime(change.detectedAt)}</p>
                    </div>
                    <p className={styles.changeSummary}>{change.summary}</p>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Attention Section */}
      {stats && stats.errorUrls > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Needs Attention</h3>
          </div>
          <Alert variant="warning">
            {stats.errorUrls} tracked URL{stats.errorUrls === 1 ? ' has' : 's have'} errors. Please
            review and fix them.
          </Alert>
        </div>
      )}

      {/* Plan Info */}
      {stats && (
        <Card className={styles.tipsCard}>
          <CardBody>
            <h3 className={styles.tipsTitle}>Plan Usage</h3>
            <p className={styles.tipsList}>
              You are on the <strong>{stats.planName}</strong> plan. Using{' '}
              <strong>{stats.totalUrls}</strong> of <strong>{stats.planLimit}</strong> tracked URLs.
            </p>
          </CardBody>
        </Card>
      )}
    </AppLayout>
  )
}
